/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { Company } from 'types/entities/Company'
import CompanyBasicDuplicatesList from './CompanyBasicDuplicatesList'

interface CompanyBasicDuplicateWarningProps {
    duplicates: Company[]
    isOpeningMergeModal: boolean
    showDuplicatesPopover: boolean
    onMouseEnter: () => void
    onMouseLeave: () => void
    onClick: () => void
    onDuplicateClick?: (company: Company) => void
    floating?: boolean
}

const CompanyBasicDuplicateWarning = ({
    duplicates,
    isOpeningMergeModal,
    showDuplicatesPopover,
    onMouseEnter,
    onMouseLeave,
    onClick,
    onDuplicateClick,
    floating = false,
}: CompanyBasicDuplicateWarningProps) => {
    const { t } = useTranslation()

    if (duplicates.length === 0) {
        return null
    }

    const buttonBaseClassName = 'animate-pulse font-semibold flex items-center hover:text-amber-700 transition-colors whitespace-nowrap'
    const popoverBaseClassName = 'absolute top-full right-0 w-80 z-[9999] p-3 bg-white dark:bg-gray-900 border border-amber-200 dark:border-amber-800 rounded-xl shadow-xl'

    const buttonClassName = clsx(
        buttonBaseClassName,
        floating
            ? 'text-sm gap-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 px-5 py-3 rounded-xl border-2 border-amber-400 dark:border-amber-500 shadow-lg hover:shadow-2xl hover:bg-amber-100 dark:hover:bg-amber-900/30'
            : 'text-[10px] text-amber-600 gap-1'
    )

    const popoverClassName = clsx(popoverBaseClassName, floating ? 'mt-2' : 'mt-1')

    return (
        <div
            className={clsx('CompanyBasicDuplicateWarning', floating ? 'fixed top-20 right-6 z-[9999]' : 'relative z-50')}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <button
                type="button"
                onClick={onClick}
                className={buttonClassName}
            >
                <ExclamationTriangleIcon className={clsx('shrink-0', floating ? 'w-5 h-5' : 'w-3.5 h-3.5')} />
                {isOpeningMergeModal ? t('Opening...') : t('Possible duplicate found!')}
            </button>

            {showDuplicatesPopover && (
                <div className={clsx(popoverClassName)}>
                    <div className="text-xs font-bold text-amber-900 dark:text-amber-100 mb-2">
                        {t('Possible duplicates:')}
                    </div>
                    <ul className="list-none space-y-2 max-h-60 overflow-y-auto UiScrollbarAlwaysVisible pr-1">
                        <CompanyBasicDuplicatesList
                            duplicates={duplicates}
                            onDuplicateClick={onDuplicateClick}
                        />
                    </ul>
                </div>
            )}
        </div>
    )
}

export default CompanyBasicDuplicateWarning
